import type { Cliente } from '@/lib/supabase/tipos-bd'
import { CeldaDoble, type ColumnaTabla } from '@/shared/ui/Tabla/Tabla'

import { useOpcionesZonas } from '@/shared/datos/useOpciones'
import { opcionesDepartamentos, opcionesCiudades } from '@/shared/datos/colombia'

import { PaginaMaestro } from '../compartido/PaginaMaestro'
import type { CampoMaestro, RegistroMaestro } from '../compartido/tipos'

type FilaCliente = Cliente & RegistroMaestro

const COLUMNAS: ColumnaTabla<FilaCliente>[] = [
  {
    encabezado: '',
    celda: (cliente) => (
      <CeldaDoble principal={cliente.nombre} secundario={`NIT / CC ${cliente.documento}`} />
    ),
  },
  {
    encabezado: 'Contacto',
    celda: (cliente) => (
      <CeldaDoble
        principal={cliente.nombre_contacto ?? '—'}
        secundario={cliente.telefono ?? cliente.correo ?? undefined}
      />
    ),
  },
  {
    encabezado: 'WhatsApp',
    celda: (cliente) => cliente.telefono_whatsapp ?? '—',
  },
  {
    encabezado: 'Dirección',
    celda: (cliente) => cliente.direccion ?? '—',
  },
]

export function PaginaClientes() {
  const { opciones: zonas } = useOpcionesZonas()

  const campos: CampoMaestro[] = [
    {
      nombre: 'documento',
      etiqueta: 'NIT o cédula',
      tipo: 'texto',
      obligatorio: true,
      medio: true,
    },
    { nombre: 'nombre', etiqueta: 'Nombre o razón social', tipo: 'texto', obligatorio: true, medio: true },
    { nombre: 'nombre_contacto', etiqueta: 'Persona de contacto', tipo: 'texto', medio: true },
    { nombre: 'telefono', etiqueta: 'Teléfono', tipo: 'telefono', medio: true },
    {
      nombre: 'telefono_whatsapp',
      etiqueta: 'WhatsApp',
      tipo: 'telefono',
      medio: true,
      ayuda: 'A este número llega el acta de conformidad para la firma.',
    },
    { nombre: 'correo', etiqueta: 'Correo', tipo: 'correo', medio: true },
    {
      nombre: 'zona_id',
      etiqueta: 'Zona',
      tipo: 'selector',
      opciones: zonas,
      medio: true,
      ayuda: 'Se usa como sugerencia al crear una orden.',
    },
    // Departamento y ciudad encadenados, como en una tienda: se elige el
    // departamento y la ciudad queda restringida a sus municipios. Van juntos y
    // antes de la dirección porque es el orden en que se dicta una dirección.
    {
      nombre: 'departamento',
      etiqueta: 'Departamento',
      tipo: 'selector',
      opciones: opcionesDepartamentos(),
      medio: true,
    },
    {
      nombre: 'ciudad',
      etiqueta: 'Ciudad',
      tipo: 'selector',
      dependeDe: 'departamento',
      opcionesSegun: opcionesCiudades,
      medio: true,
      ayuda: 'Primero elija el departamento.',
    },
    { nombre: 'direccion', etiqueta: 'Dirección', tipo: 'texto' },
    { nombre: 'notas', etiqueta: 'Notas', tipo: 'area' },
  ]

  return (
    <PaginaMaestro<FilaCliente>
      titulo="Clientes"
      subtitulo="A quién se le presta el servicio. El WhatsApp es el número al que llega el acta para la firma."
      tabla="clientes"
      ordenarPor="nombre"
      singular="cliente"
      columnas={COLUMNAS}
      campos={campos}
      textoDeBusqueda={(cliente) =>
        [cliente.nombre, cliente.documento, cliente.nombre_contacto, cliente.direccion]
          .filter(Boolean)
          .join(' ')
      }
    />
  )
}
